/**
 * AllocationBoardConnected — the Schichtstart board wired to an allocation
 * session, for the Home detail pane (the agent working IN the frame; roster
 * precedent). The board IS the meeting surface: ward state from
 * GET /locations/{id}/allocation, the agent's latest proposals folded from
 * the session's allocation_commands blocks as ghost suggestions, tap-to-assign
 * for the lead. The chat input below is how the lead talks to the agent
 * ("verteile nach Kontinuität") — prose replies stream as a bubble; the board,
 * not a transcript, carries the state.
 *
 * Author vs viewer: the lead (author) assigns; a shared viewer (or a ward
 * nurse mirroring the board) gets read-only — the server's can_manage gate on
 * the PUT is the real boundary, this just hides dead controls.
 *
 * `session` is optional (AccessBoardConnected / TeamAccessBoardConnected
 * precedent, same session-less Home render): the ward read itself doesn't need
 * one (useUnitScope already falls back profile unit → managed-units catalogue,
 * and PUT /patients/{id}/assignment — the actual custody write AllocationBoard
 * performs — is patient-scoped, not session-scoped), so the board renders and
 * is fully assignable session-less. Only the SESSION's own unit link
 * (PUT /sessions/{id}/location) and the chat side-channel need a real session;
 * a session-less pick just stays local, exactly like TeamAccessBoardConnected.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { sessionKeys } from '@/hooks/useSessions';
import { useStreamingMessage } from '@/hooks/useStreamingChat';
import { useDeferredFirstSend } from '@/hooks/useDeferredFirstSend';
import { useAuthStore } from '@/stores/auth';
import { useWardAllocation, shiftKeys } from '@/hooks/useShifts';
import {
  isUnitAuthorityDenied,
  useUnitScope,
} from '@/hooks/useAccess';
import { mainAppUrl } from '@/api/mainAppUrl';
import { ALLOCATION_AGENT_SLUG } from '@/lib/sessionRouting';
import { BoardAgentChat, ChatInput } from '@/components/chat';
import { Skeleton } from '@/components/ui/skeleton';
import { NoUnitAuthority, UnitChooser } from '@/components/access/UnitScopePanels';
import type { SessionResponse } from '@/api/types_session';
import type { SessionDetail } from '@/hooks/useSessions';

import { foldAllocation } from './foldAllocation';
import { AllocationBoard } from './AllocationBoard';

interface AllocationBoardConnectedProps {
  /** Present on the Session page / detail pane (enables the session's own unit
   *  link); absent on the session-less Home face (the unit pick stays local
   *  instead of persisting — the composer below still works, see
   *  useDeferredFirstSend). */
  session?: SessionDetail;
  /** Face mode only — the patient frame a deferred create should carry. */
  facePatientId?: string | null;
  /** Face mode only — fires once the composer's first send mints the session. */
  onSessionCreated?: (session: SessionResponse) => void;
}

export function AllocationBoardConnected({
  session,
  facePatientId,
  onSessionCreated,
}: AllocationBoardConnectedProps) {
  const { t } = useTranslation();
  const sessionId = session?.session.id;
  const streaming = useStreamingMessage(sessionId ?? '');
  const queryClient = useQueryClient();
  const me = useAuthStore((s) => s.user);

  // The board shows a ward: the session→location link that used to persist
  // the pick is gone with the session cut-over (session_assign was a DELETE
  // row — a session cites a subject now, nothing frames it to a unit). The
  // pick is the profile unit → managed-units catalogue → a LOCAL choice
  // (TeamAccessBoardConnected precedent).
  const [localUnitId, setLocalUnitId] = useState<string | undefined>();
  const scope = useUnitScope(localUnitId ?? undefined);
  const locationId = scope.status === 'ready' ? scope.unit.id : undefined;
  const ward = useWardAllocation(locationId);

  const deferred = useDeferredFirstSend({
    sessionId,
    agentSlug: ALLOCATION_AGENT_SLUG,
    patientId: facePatientId ?? undefined,
    sendMessage: streaming.sendMessage,
    onCreated: (newSession) => {
      onSessionCreated?.(newSession);
    },
  });

  // Fail CLOSED on a missing author_id on a session surface: allocation
  // sessions are all post-deploy (author always recorded), and misleading
  // assign affordances during shift start are worse than a hidden button. The
  // server's can_manage gate on the PUT remains the real boundary. A
  // session-less board is the caller's own surface, so they always "author"
  // the local pick (TeamAccessBoardConnected precedent).
  const isAuthor = session ? session.session.created_by.performer === me?.id : true;

  // Ghost proposals: latest allocation_commands per patient across the session.
  const proposals = useMemo(() => foldAllocation(session?.messages.items), [session?.messages]);

  // When a turn finishes, the agent may have emitted new proposals AND the
  // lead may have executed some between turns — refresh both reads.
  const wasStreaming = useRef(false);
  useEffect(() => {
    if (streaming.isStreaming) {
      wasStreaming.current = true;
    } else if (wasStreaming.current) {
      wasStreaming.current = false;
      if (session?.session.id) {
        void queryClient.invalidateQueries({ queryKey: sessionKeys.detail(session.session.id) });
      }
      void queryClient.invalidateQueries({ queryKey: shiftKeys.all });
    }
  }, [streaming.isStreaming, session?.session.id, queryClient]);

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="min-h-0 flex-1 overflow-auto">
        {scope.status === 'loading' ? (
          <WardSkeleton />
        ) : scope.status === 'error' ? (
          <WardLoadRetry onRetry={scope.retry} />
        ) : scope.status === 'none' ? (
          isAuthor ? (
            <NoUnitAuthority
              onOpenProfile={() => window.location.assign(mainAppUrl('/profile'))}
              profileUnitLabel={scope.profileUnitLabel}
            />
          ) : (
            <NoWardMessage />
          )
        ) : scope.status === 'choose' ? (
          isAuthor ? (
            <UnitChooser
              options={scope.options}
              onPick={setLocalUnitId}
            />
          ) : (
            <NoWardMessage />
          )
        ) : ward.data ? (
          <AllocationBoard ward={ward.data} proposals={proposals} canAssign={isAuthor} />
        ) : ward.isError && isUnitAuthorityDenied(ward.error) ? (
          // Not a load failure and not retryable: the server refused this unit,
          // and its sentence is the one that tells her what to do about it.
          <NoUnitAuthority
            onOpenProfile={() => window.location.assign(mainAppUrl('/profile'))}
            deniedMessage={ward.error.message}
          />
        ) : ward.isError ? (
          <WardLoadRetry onRetry={() => void ward.refetch()} />
        ) : (
          <WardSkeleton />
        )}
      </div>

      {session && isAuthor && (
        <BoardAgentChat
          session={session}
          streaming={streaming}
          className="max-h-[35%] shrink-0 overflow-y-auto border-t border-neutral-200 bg-neutral-50 px-4 py-2"
        />
      )}

      {/* Composer only once the board is grounded in a ward — an ungrounded
          agent would propose without patients/nurses; the chooser above is the
          call to action until then. Session or a way to create one (face mode
          passes onSessionCreated) — see CarePlanBoardConnected for the same gate. */}
      {isAuthor && locationId && (session || onSessionCreated) && (
        <div className="shrink-0 border-t border-neutral-200 bg-neutral-50 px-4 py-3 pb-safe">
          <ChatInput
            onSend={(content) => void deferred.send(content)}
            isPending={streaming.isStreaming || deferred.isPending}
            placeholder={t('allocation.board.chatPlaceholder', {
              defaultValue: 'Ask for a distribution proposal…',
            })}
          />
        </div>
      )}
    </div>
  );
}

function WardSkeleton() {
  return (
    <div className="space-y-3 p-4" aria-busy="true">
      <Skeleton className="h-20 w-full rounded-xl" />
      <Skeleton className="h-16 w-full rounded-xl" />
      <Skeleton className="h-16 w-full rounded-xl" />
    </div>
  );
}

function WardLoadRetry({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="p-4">
      <p className="text-[15px] text-neutral-700" role="alert">
        {t('allocation.board.loadError', {
          defaultValue: "Could not load the ward's allocation.",
        })}{' '}
        <button
          type="button"
          onClick={onRetry}
          className="font-semibold text-primary underline-offset-2 hover:underline"
        >
          {t('actions.retry', 'Retry')}
        </button>
      </p>
    </div>
  );
}

/** Read-only viewer of an ungrounded session — nothing they can act on. */
function NoWardMessage() {
  const { t } = useTranslation();
  return (
    <div className="grid h-full place-items-center p-8 text-center">
      <p className="text-[15px] text-neutral-500">
        {t('allocation.board.noWard', {
          defaultValue: 'No unit assigned to your profile yet.',
        })}
      </p>
    </div>
  );
}
